import { type FC, useCallback, useEffect, useState } from 'react';

import { type SelectValue } from '@mui/base';
import { type SelectChangeEventType } from '@mui/base/useSelect/useSelect.types';
import { TripFilterMenu } from '@vakantiesnl/components/src/molecules/TripFilterMenu';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { usePartyComposition } from '@vakantiesnl/services/src/stores';
import { useAccoOfferStore } from '@vakantiesnl/services/src/stores/accommodationStore';
import { buildSummarizedPartyString } from '@vakantiesnl/services/src/util/partyUtils';
import { type MicroCopy, type Search } from '@vakantiesnl/types';
import { isEqual } from 'lodash';

type Props = {
	microCopies: MicroCopy;
};

export const TripFilterMenuBlock: FC<Props> = ({ microCopies }) => {
	const {
		state: tripFiltersState,
		computed: { durations },
		dispatch: {
			updatePartyComposition,
			updateDepartureDateTripFilter,
			updateDurationRangeTripFilter,
			updateInvalidPreselection,
			updateSelectedTrip,
			updateSelectedFlight,
			updateSelectedRoomType,
			updateSelectedMealPlan,
		},
	} = useTripFilterContext();
	const party = usePartyComposition();
	const [partyString, setPartyString] = useState(buildSummarizedPartyString(party, microCopies));
	const clearOffers = useAccoOfferStore((s) => s.clearOffers);

	/** Upon a manual filter selection, clear the query parameters used for shareable URL */
	const clearSelectedOffer = useCallback(() => {
		updateInvalidPreselection(false);
		clearOffers();
		updateSelectedFlight(undefined);
		updateSelectedRoomType(undefined);
		updateSelectedMealPlan(undefined);
	}, [updateInvalidPreselection, clearOffers, updateSelectedFlight, updateSelectedRoomType, updateSelectedMealPlan]);

	const onSetDepartureDate = useCallback(
		(departureDate: string[]) => {
			/** When the departureDate is equal return */
			if (isEqual(departureDate, tripFiltersState.departureDate)) return;
			if (departureDate.length) {
				updateDepartureDateTripFilter([departureDate[0]]);
				/** Remove pre-selection of price bar date */
				updateSelectedTrip(undefined);
				clearSelectedOffer();
			}
		},
		[clearSelectedOffer, tripFiltersState.departureDate, updateDepartureDateTripFilter, updateSelectedTrip],
	);

	const onChangeDurationRange = useCallback(
		(_: SelectChangeEventType, options: SelectValue<string, true>) => {
			const selectedDurationRange = [...options].map((item) => parseInt(item)).sort((a, b) => a - b);
			updateDurationRangeTripFilter(selectedDurationRange);
			clearSelectedOffer();
		},
		[clearSelectedOffer, updateDurationRangeTripFilter],
	);

	const onPartyUpdate = useCallback(
		(party: Search.PartyComposition[]) => {
			setPartyString(buildSummarizedPartyString(party, microCopies));
		},
		[microCopies],
	);

	const onClosePartyPicker = useCallback(
		(updatedParty: Search.PartyComposition[]) => {
			/** When the party is equal return */
			if (isEqual(updatedParty, party)) return;
			updatePartyComposition(updatedParty);
			clearSelectedOffer();
		},
		[clearSelectedOffer, party, updatePartyComposition],
	);

	// Update the party composition in the partypicker when the party composition is being read from local storage
	useEffect(() => {
		if (party) {
			setPartyString(buildSummarizedPartyString(party, microCopies));
		}
	}, [party, microCopies]);

	return (
		<TripFilterMenu
			departureDate={tripFiltersState.departureDate}
			party={party}
			durations={durations}
			partyString={partyString}
			onPartyUpdate={onPartyUpdate}
			onChangeDurationRange={onChangeDurationRange}
			onClosePartyPicker={onClosePartyPicker}
			onSetDepartureDate={onSetDepartureDate}
			microCopies={microCopies}
			airportOptions={null}
			onChangeAirport={undefined}
			mealplanOptions={null}
			onChangeMealplan={undefined}
			hideMultiRoom
		/>
	);
};
